import { AnimatePresence, motion } from "framer-motion";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { IconEye, IconEyeSlash } from "../../../assets/icons/icons";
import { fetchMajorAuction } from "../../../models/products.model";
import {
  checkIn,
  login,
  resetPassword,
  submitMagicLink
} from "../../../models/signup.model";
import { UserContext } from "../../../services/AppContext/appProvider";
import { useTheme } from "../../../services/ThemeContext";
import { systemErrorAlertText } from "../../../utilities/utils";

type ModalLoginProps = {
  closeModal: () => void;
  openRegistrationModal?: () => void;
  modalType: "modal" | "page";
};

const ModalLogin: React.FC<ModalLoginProps> = ({
  closeModal,
  openRegistrationModal,
  modalType
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errorEmail, setErrorEmail] = useState<string>("");
  const [errorPassword, setErrorPassword] = useState<string>("");
  const [showMagicLink, setShowMagicLink] = useState<boolean>(true);
  const nav = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { theme } = useTheme();
  const { setCurrentPage } = useContext(UserContext);

  function validateEmail(email: string): boolean {
    let isValid = true;
    if (!email) {
      setErrorEmail("Email is required");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setErrorEmail("Email is invalid");
      isValid = false;
    }
    return isValid;
  }

  function validateForm(): boolean {
    let isValid = true;

    if (!validateEmail(email)) {
      isValid = false;
    }

    if (!password) {
      setErrorPassword("Password is required");
      isValid = false;
    }
    return isValid;
  }

  async function handleLogin() {
    console.log("login");
    console.log(email);
    console.log(password);

    if (!validateForm()) {
      return;
    }
    localStorage.removeItem("profileImage");
    // send request to database server
    const response = await login(email, password);

    if (response.rStatus === "success") {
      // Fetch major auctions after successful login
      const majorAuctionResponse = await fetchMajorAuction();
      console.log(majorAuctionResponse);
      if (
        majorAuctionResponse.rStatus === "success" &&
        majorAuctionResponse.rData
      ) {
        console.log("Major Auctions fetched successfully");
        const nextAuction = majorAuctionResponse.rData;
        localStorage.setItem("nextMajorAuction", JSON.stringify(nextAuction));
      }
      // close modal
      if (closeModal) {
        
        closeModal();
        setCurrentPage("");
        // refresh page
        window.location.reload();
      } else {
        // navigate to home
        nav("/");
        window.location.reload();
      }

      Swal.close();

      checkIn();
      // if (checkedIn.rStatus === "success") {
      //   console.log("Checked in successfully");
      //   nav("/dashboard?page=rewards");
      // }
    } else {
      Swal.close();
      Swal.fire({
        title: t("Error"),
        html: `<strong class="font-extrabold">${response.rMessage || t("An error occurred")}</strong> ${systemErrorAlertText()}`,
        icon: "error",
        confirmButtonText: t("OK"),
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      });
    }
  }

  async function handleResetPassword() {
    console.log("reset password");
    console.log(email);
    if (!validateEmail(email)) {
      return;
    }
    // strip all whitespace from email
    const response = await resetPassword(email.replace(/\s/g, ""));
    if (response.rStatus === "success") {
      Swal.fire({
        title: t("Reset email sent"),
        text: t(
          "Please check your email. Check your spam/junk folder if you don't see it in your inbox."
        ),
        icon: "success",
        confirmButtonText: "OK",
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      });
    } else {
      Swal.fire({
        title: t("Error"),
        html: `<strong class="font-extrabold">${response.rMessage || t("An error occurred")}</strong> ${systemErrorAlertText()}`,

        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      });
    }
  }

  async function handleMagicLink() {
    console.log("Magic Link");
    console.log(email);

    if (!validateEmail(email)) {
      return;
    }

    console.log("Magic Link");

    const response = await submitMagicLink(email);
    console.log(response);
    if (response.rStatus === "success") {
      Swal.fire({
        title: t("Magic Link sent"),
        text: t("Please check your email"),
        icon: "success",
        confirmButtonText: t("OK"),
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      }).then(() => {
        if (closeModal) {
          setCurrentPage("");
          closeModal();
        } else {
          // navigate to home
          nav("/");
          window.location.reload();
        }
      });
    } else {
      Swal.fire({
        title: t("Error"),
        html: `<strong class="font-extrabold">${response.rMessage || t("An error occurred")}</strong> ${systemErrorAlertText()}`,
        icon: "error",
        confirmButtonText: t("OK"),
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      });
    }
  }

  function switchModal() {
    if (closeModal && openRegistrationModal) {
      openRegistrationModal();
      setCurrentPage("");
      closeModal();
    }
  }

  // track current page
  useEffect(() => {
    setCurrentPage("login");
  },[]);


  // Monitor changes in the password field to show/hide the Magic Link button
  useEffect(() => {
    setShowMagicLink(password.length === 0);
  }, [password]);

  return (
    <>
      {modalType === "page" && <div className="h-full xl:h-[62vh]"></div>}
      <div
        className={`modal modal-open !overflow-hidden ${modalType === "page" && "!bg-transparent"}`}
        dir="auto"
        style={{
          marginRight: "-17px"
        }}
      >
        <div className={`modal-box max-w-md rounded-lg`} dir="auto">
          {closeModal && (
            <div className="modal-action mt-0">
              <label
                className="btn btn-circle btn-sm absolute right-2 top-2 text-secondary"
                onClick={() =>{setCurrentPage(""); closeModal();}}
              >
                ✕
              </label>
            </div>
          )}

          <h3
            className="mb-3 text-center text-2xl font-bold text-secondary"
            dir="auto"
          >
            {t("Log in")}
          </h3>
          <div className="form-control mb-3">
            <label className="label">
              <span className="label-text text-secondary">Email Address *</span>
            </label>
            <input
              type="email"
              placeholder="Email Address"
              className="input input-bordered"
              id="exampleInputEmail1"
              aria-describedby="emailHelp"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errorEmail && (
              <label className="label text-error">{errorEmail}</label>
            )}
          </div>
          <div className="form-control mb-3">
            {/* <label className="label">
              <span className={`label-text text-secondary`}>Password</span>
            </label> */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="input input-bordered w-full"
                id="inputPassword1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
              <div
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-0 flex h-full items-center"
              >
                {showPassword ? (
                  <IconEye
                    className={`w-6 cursor-pointer opacity-60 transition-all hover:opacity-100 ${theme === "dark" ? "fill-white/90" : "fill-neutral/90"}`}
                  />
                ) : (
                  <IconEyeSlash
                    className={`w-6 cursor-pointer opacity-60 transition-all hover:opacity-100 ${theme === "dark" ? "fill-white/90" : "fill-neutral/90"}`}
                  />
                )}
              </div>
            </div>
            {errorPassword && (
              <label className="label text-error">{errorPassword}</label>
            )}
            <button
              type="button"
              className={`link text-right text-secondary ${!email && "disabled cursor-default !text-gray-400"} m-auto mb-3 mr-0 mt-3 w-fit`}
              onClick={handleResetPassword}
              disabled={!email}
            >
              {t("Forgot your password?")}
            </button>
          </div>

          <div className="modal-action mt-0 justify-between">
            {/* Conditionally render the Magic Link button based on showMagicLink state */}
            <div>
              <AnimatePresence>
                {showMagicLink && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15, ease: "easeInOut" }}
                  >
                    <button
                      type="submit"
                      className="btn btn-sm animate-bounce rounded-lg border-none bg-secondary text-base-100 hover:bg-secondary hover:text-base-100 disabled:bg-secondary disabled:text-base-100 disabled:opacity-50"
                      onClick={handleMagicLink}
                      disabled={!email}
                    >
                      <span>Magic Link &rarr;</span>
                      <div className="absolute inset-0 overflow-hidden rounded-lg">
                        <div className="bg-radial-gradient absolute inset-0 m-auto h-full w-full opacity-0 group-hover:animate-shine group-hover:opacity-50"></div>
                      </div>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div>
              <button
                type="submit"
                className="btn btn-secondary btn-sm rounded-lg border-none bg-secondary text-base-100 disabled:bg-secondary disabled:text-base-100 disabled:opacity-50"
                onClick={handleLogin}
                disabled={!email || !password}
              >
                {t("Login")}
              </button>
            </div>
          </div>
          <div>
            <div className="m-auto mt-3 flex justify-center gap-2 text-center">
              {t("Don't have an account?")}
              <button className="link text-secondary" onClick={switchModal}>
                {t("Sign up")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalLogin;
